<template>
  <ModalBase @close-modal="close" show-modal narrow auto-h class="survey-modal">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal title-modal--simple">
        <template v-slot:title>
          <div style="display: flex; flex-direction: column; gap: 0.5rem">
            <span>{{ reportModalTitle }}</span>
            <span v-if="reportModalSubtitle" style="color: #b9b4b4; font-size: 1rem">{{ reportModalSubtitle }}</span>
          </div>
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <div ref="surveyStepContainer" class="survey-step-container">
        <transition
          name="survey-step"
          mode="out-in"
          @before-enter="beforeStepEnter"
          @before-leave="beforeStepLeave"
          @enter="onStepEnter"
          @after-leave="afterStepLeave"
          @after-enter="afterStepEnter"
        >
          <div
            :key="`step-${currentStep && currentStep.id ? currentStep.id : currentStepIdx}-${isComplete}`"
            class="survey-step-panel"
          >
            <SurveyStep
              @next-step="handleNextStep"
              @prev-step="prevStep"
              @skip-step="skipStep"
              @save-answer="saveAnswer"
              :step-data="currentStep"
              @close="close"
            />
            <div v-if="isComplete">
              <div class="answer-list-wrap">
                <ul :key="answerkey" class="answer-list">
                  <li v-for="(answer, index) in answers" :key="'answer-' + index" :class="getAnswerClass(answer.id)">
                    <div class="answer-list--question">{{ answer.question }}:</div>
                    <div class="answer-list--answer">
                      <font-awesome-icon v-if="answer.value === 'true'" icon="fa-solid fa-check" />
                      <font-awesome-icon v-else-if="answer.value === 'false'" icon="fa-solid fa-xmark" />
                      <div v-else>€ {{ answer.value == null ? 0 : answer.value }}</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </template>

    <template v-slot:actions>
      <hr />
      <div class="survey-modal-footer">
        <GekkoButton
          v-if="currentStepIdx > 0"
          @click="prevStep"
          :text="$gettext('Back')"
          icon="fa-solid fa-arrow-left"
          variant="link"
          class="px-0"
          size="sm"
        />
        <GekkoButton v-else @click="close" :text="$gettext('Cancel')" variant="link" size="sm" class="text-danger" />
        <div>
          <small>
            {{ pageNumber }}
          </small>
        </div>
        <GekkoButton
          v-if="!isComplete"
          :key="isComplete"
          :text="$gettext('Next')"
          :disabled="nextDisabled"
          @click="handleNextStep"
          icon="fa-solid fa-arrow-right"
          icon-right
        />
        <GekkoButton
          v-else
          @click="submit"
          :text="$gettext('Generate report')"
          icon="fa-solid fa-chart-column"
          class="generate-report-btn"
        />
      </div>
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import SurveyStep from '@/components/ui/survey/SurveyStep'

import ModalBase from '@/components/base_components/ModalBase'
import TitleBar from '@/components/TitleBar'
import GekkoButton from '@/components/GekkoButton'
import CostService from '@/store/services/cost.service'
import TripService from '@/store/services/trip.service'

export default {
  name: 'SurveyModal',
  components: {
    ModalBase,
    TitleBar,
    GekkoButton,
    SurveyStep,
  },
  data() {
    return {
      answerkey: 0,
      km_total: 0,
      km_private: 0,
      car_costs: 0,
      incomplete_costs: false,
      reverse_charge_totals: null,
      gifts_exceed_totals: null,
      startDate: null,
      endDate: null,
      stepResizeObserver: null,
      resizeRafId: null,
    }
  },
  computed: {
    ...mapGetters({
      reportInitialValues: 'taxreports/getReportInitialValues',
      currentCompany: 'main/getCurrentCompany',
      getCompanyId: 'main/getCompanyId',
    }),
    ...mapGetters('survey', {
      currentStepIdx: 'getCurrentStepIdx',
      steps: 'getSteps',
      answers: 'getAnswers',
      answerValues: 'getAnswerValues',
    }),
    pageNumber() {
      var text = this.$gettext('Step %{stepNumber} of %{stepTotal}')
      text = text.replace('%{stepNumber}', this.currentStepIdx + 1)
      text = text.replace('%{stepTotal}', this.steps.length)
      return text
    },
    reportModalTitle() {
      let title = ''
      let selectedPeriod = ''
      if (this.reportInitialValues.month) {
        const date = new Date()
        // const locale = navigator.language || navigator.userLanguage;
        date.setMonth(this.reportInitialValues.month - 1)
        selectedPeriod = date.toLocaleString(this.$language.current, { month: 'short' })
      } else {
        selectedPeriod = this.reportInitialValues.quarter
      }
      title = this.$gettext('Report for %{period} %{year}')
      title = this.$gettextInterpolate(title, { period: selectedPeriod, year: this.reportInitialValues.year })
      return title
    },
    reportModalSubtitle() {
      let subtitle = ''
      if (this.currentStep && this.currentStep.subtitle) {
        subtitle = this.currentStep.subtitle
      }
      return subtitle
    },
    isFullYear() {
      return (
        (this.reportInitialValues.month && this.reportInitialValues.month === 12) ||
        (this.reportInitialValues.quarter && this.reportInitialValues.quarter === 'Q4')
      )
    },
    currentStep() {
      return this.steps[this.currentStepIdx]
    },
    isComplete() {
      return this.currentStepIdx + 1 === this.steps.length
    },
    overGiftLimitText() {
      let title = ''
      const receiptWord = this.gifts_exceed_totals.count === 1 ? this.$gettext('receipt') : this.$gettext('receipts')

      title = this.$gettext('Representational gifts: %{amount} EUR (%{count} %{ receipt_word })')
      title = this.$gettextInterpolate(title, {
        count: this.gifts_exceed_totals.count,
        amount: this.gifts_exceed_totals.total_net,
        receipt_word: receiptWord,
      })
      title = title + '<br>' + this.$gettext('Would you like to adjust de VAT deduction?')
      return title
    },
    nextDisabled () {
      const currentQuestion = this.currentStep?.question
      const currentAnswerValue = this.currentStep?.id ? this.answers[this.currentStep.id]?.value : null

      return Boolean(currentQuestion?.options?.length) && currentAnswerValue == null
    }
  },
  mounted() {
    document.body.classList.add('modal-on')
    this.$nextTick(() => {
      const panel = this.$el.querySelector('.survey-step-panel')
      if (panel) {
        this.observeStepPanel(panel)
        this.setStepContainerHeight(panel.offsetHeight)
      }
    })
  },
  beforeDestroy() {
    this.disconnectStepObserver()
    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId)
      this.resizeRafId = null
    }
    this.storeResetSurvey()
  },
  created() {
    var params = {
      company_profile: this.currentCompany.id,
      year: this.reportInitialValues.year,
      owner_company: this.currentCompany.id,
      start_date: this.reportInitialValues.year + '-01-01',
      end_date: this.reportInitialValues.year + '-12-31',
      transport_type: 'car',
    }
    let km_total = TripService.getTotalKmBusiness(params)
    let km_private = TripService.getTotalKmPrivate(params)
    params = {
      owner_company: this.getCompanyId,
      cost_type__default_name: 'car',
      start_date: this.reportInitialValues.year + '-01-01',
      end_date: this.reportInitialValues.year + '-12-31',
      transport_type: 'car',
    }
    let car_costs = CostService.totalNet(params)
    this.setupDates()
    params = {
      owner_company: this.getCompanyId,
      start_date: this.startDate,
      end_date: this.endDate,
      tax_exempt: false,
      tax_exists: false,
    }

    let incomplete_costs = CostService.hasIncompleteCosts({ ...params, tax_exists: null })
    let reverse_charge_totals = CostService.reverseChargeTotals(params)
    let gifts_exceed_totals = CostService.totalAmounts({
      owner_company: this.getCompanyId,
      start_date: this.startDate,
      end_date: this.endDate,
      cost_type__default_name: 'represent',
    })

    Promise.all([km_total, km_private, car_costs, incomplete_costs, reverse_charge_totals, gifts_exceed_totals]).then(
      (responses) => {
        this.km_total = responses[0].data
        this.km_private = responses[1].data
        this.car_costs = Math.floor(responses[2].data)
        this.incomplete_costs = responses[3].data
        this.reverse_charge_totals = responses[4].data
        this.gifts_exceed_totals = responses[5].data
        this.initSteps()
      }
    )
  },
  methods: {
    ...mapActions('survey', {
      storeSaveAnswer: 'saveAnswer',
      storeSetCurrentStepIdx: 'setCurrentStepIdx',
      storeSetSteps: 'setSteps',
      storeSetAnswers: 'setAnswers',
      storeResetSurvey: 'resetSurvey',
    }),
    getDaysInMonth(year, month) {
      var d = new Date(year, month, 0).getDate()
      return d
    },
    setupDates() {
      if (this.reportInitialValues.month != null) {
        this.startDate = this.reportInitialValues.year + '-' + this.reportInitialValues.month + '-' + 1
        this.endDate =
          this.reportInitialValues.year +
          '-' +
          this.reportInitialValues.month +
          '-' +
          this.getDaysInMonth(this.reportInitialValues.year, this.reportInitialValues.month)
      } else {
        var q = this.reportInitialValues.quarter
        const quarter = q.split('Q')[1]
        var endMonth = quarter * 3
        this.startDate = this.reportInitialValues.year + '-' + (endMonth - 2) + '-' + 1
        this.endDate =
          this.reportInitialValues.year +
          '-' +
          endMonth +
          '-' +
          this.getDaysInMonth(this.reportInitialValues.year, endMonth)
      }
    },
    close() {
      document.body.classList.remove('modal-on')
      this.storeResetSurvey()
      if (this.$route.path.includes('control-panel'))
        this.$router.push({ name: 'ControlPanelVAT', params: { customerId: this.getCompanyId } })
      else this.$router.push({ name: 'TaxreportList' })
    },
    submit() {
      console.log('submit')
      const idsToSanitize = [
        'car_net',
        'gift_paid',
        'car_vat',
        'no_supplier_import_inside_eu',
        'no_supplier_import_outside_eu',
        'private_expenses_net',
        'private_expenses_vat',
      ]

      const sanitizedAnswerValues = this.answerValues.map((answer) => {
        if (idsToSanitize.includes(answer.id) && answer.value === null) {
          return { ...answer, value: 0 }
        }
        return answer
      })

      this.$emit('submit', sanitizedAnswerValues)
    },
    handleNextStep() {
      console.log('handleNextStep')
      const currentStep = this.steps[this.currentStepIdx]
      const currentQuestion = currentStep?.question
      const currentAnswerValue = currentStep?.id ? this.answers[currentStep.id]?.value : null
      
      if (currentQuestion?.options?.length && currentAnswerValue !== null && currentAnswerValue !== undefined) {
        const selectedOption = currentQuestion.options.find(
          (option) => String(option.value) === String(currentAnswerValue)
        )
        if (selectedOption?.fn?.name === 'nextStep') {
          this.nextStep()
        }
        if (selectedOption?.fn?.name === 'skipStep') {
          this.skipStep()
        }
        if (selectedOption?.fn?.name === 'close') {
          this.close()
        }
      } else if (!currentQuestion || currentQuestion.type === 'dialog') {
        this.nextStep()
      }
    },
    nextStep () {
      if (this.currentStepIdx < this.steps.length - 1) {
        this.storeSetCurrentStepIdx(this.currentStepIdx + 1)
      }
    },
    prevStep() {
      if (this.currentStepIdx > 0) {
        const previousStep = this.steps[this.currentStepIdx - 1]

        // If the previous step is conditional (a subcomponent shown after a yes/no question),
        // go back to it only when its condition answer is true. Otherwise skip it.
        if (previousStep?.condition) {
          const conditionAnswerValue = this.answers[previousStep.condition]?.value
          if (String(conditionAnswerValue) === 'true') {
            this.storeSetCurrentStepIdx(this.currentStepIdx - 1)
          } else {
            if (this.currentStepIdx > 1) {
              this.storeSetCurrentStepIdx(this.currentStepIdx - 2)
            }
          }
        } else {
          this.storeSetCurrentStepIdx(this.currentStepIdx - 1)
        }
      }
    },
    skipStep() {
      if (this.currentStepIdx < this.steps.length - 2) {
        this.storeSetCurrentStepIdx(this.currentStepIdx + 2)
      }
    },
    getAnswerClass(answerId) {
      let answerClass = ''
      const subAnswerIds = [
        'import_inside_nl',
        'import_inside_eu',
        'import_outside_eu',
        'gift_paid',
        'car_net',
        'car_vat',
        'private_expenses_net',
        'private_expenses_vat',
      ]
      if (subAnswerIds.includes(answerId)) {
        answerClass = 'subanswer'
      }
      const hiddenSubAnswerIds = [
        'small_business_discount',
        'no_supplier_import_inside_eu',
        'no_supplier_import_outside_eu',
      ]
      if (hiddenSubAnswerIds.includes(answerId)) {
        answerClass = 'hide-answer'
      }
      return answerClass
    },
    saveAnswer(option) {
      if (!option || option.id === undefined || option.id === null || option.id === '') {
        return
      }

      const new_answer = {
        ...option,
        id: option.id,
        question: option.question || option.text,
        value: option.value,
      }
      this.storeSaveAnswer(new_answer)
    },
    setStepContainerHeight(height) {
      const container = this.$refs.surveyStepContainer
      if (!container || !height) return
      if (this.resizeRafId) {
        cancelAnimationFrame(this.resizeRafId)
      }
      this.resizeRafId = requestAnimationFrame(() => {
        container.style.height = `${Math.ceil(height)}px`
      })
    },
    disconnectStepObserver() {
      if (this.stepResizeObserver) {
        this.stepResizeObserver.disconnect()
        this.stepResizeObserver = null
      }
    },
    observeStepPanel(panelEl) {
      this.disconnectStepObserver()
      if (typeof ResizeObserver === 'undefined' || !panelEl) return
      this.stepResizeObserver = new ResizeObserver((entries) => {
        if (!entries || !entries.length) return
        this.setStepContainerHeight(entries[0].contentRect.height)
      })
      this.stepResizeObserver.observe(panelEl)
    },
    beforeStepEnter() {
      const container = this.$refs.surveyStepContainer
      if (!container) return
      if (!container.style.height || container.style.height === 'auto') {
        container.style.height = `${container.offsetHeight}px`
      }
    },
    beforeStepLeave(el) {
      const container = this.$refs.surveyStepContainer
      if (!container || !el) return

      const rect = el.getBoundingClientRect()
      container.style.height = `${rect.height}px`
      this.disconnectStepObserver()

      el.style.position = 'absolute'
      el.style.left = '0'
      el.style.top = '0'
      el.style.width = `${rect.width}px`
    },
    onStepEnter(el) {
      const container = this.$refs.surveyStepContainer
      if (!container) return

      this.observeStepPanel(el)
      this.$nextTick(() => {
        this.setStepContainerHeight(el.offsetHeight)
      })
    },
    afterStepLeave(el) {
      if (!el) return
      el.style.position = ''
      el.style.left = ''
      el.style.top = ''
      el.style.width = ''
    },
    afterStepEnter() {
      const panel = this.$el.querySelector('.survey-step-panel')
      if (panel) this.setStepContainerHeight(panel.offsetHeight)
    },
    initSteps() {
      let small_business_discount = 'false'
      if (this.reportInitialValues.year <= 2019) {
        small_business_discount = 'true'
      }
      const new_answer = {
        id: 'small_business_discount',
        question: this.$gettext('Small Business discount'),
        value: small_business_discount,
      }
      this.storeSaveAnswer(new_answer)

      let steps = [
        {
          type: 'question',
          id: 'treat_non_dutch_as_dutch',
          subtitle: this.$gettext('VAT over non-Dutch customers'),
          question: {
            text: this.$gettext('Do you want to declare VAT from non-Dutch customers to the Belastingdienst?'),
            extra_info: this.$gettext(
              'Choose "yes" if you want to directly declare VAT from foreign customers to the Dutch tax office. Choose "no" if you want to declare them separately via the One Stop Shop (OSS). Most smaller businesses choose "yes"'
            ),
            type: 'choose-one',
            options: [
              { text: this.$gettext('Yes'), value: 'true', fn: { name: 'nextStep' } },
              { text: this.$gettext('No'), value: 'false', fn: { name: 'nextStep' } },
            ],
          },
        },
        {
          type: 'subcomponent',
          id: 'import_without_vat_and_supplier',
          subtitle: this.$gettext('Expenses that have been marked reverse charged'),
          component: {
            name: 'GoodsImported',
            data: {
              reverse_charge_totals: this.reverse_charge_totals,
            },
          },
        },
        {
          type: 'question',
          id: 'over_gift_limit',
          subtitle: this.$gettext('Gifts exceed €227?'),
          question: {
            text: this.overGiftLimitText,
            extra_info: this.$gettext(
              'If you spend more than 227 EUR (ex VAT) on representational gifts for a single person, VAT is no longer deductible. You should lower your VAT deductions if this is the case and you can do that by selecting "yes"'
            ),
            type: 'choose-one',
            options: [
              { text: this.$gettext('Yes'), value: 'true', fn: { name: 'nextStep' } },
              { text: this.$gettext('No'), value: 'false', fn: { name: 'skipStep' } },
            ],
          },
        },
        {
          type: 'subcomponent',
          condition: 'over_gift_limit',
          id: 'over_gift_limit_component',
          subtitle: this.$gettext('Expenses that have been marked reverse charged'),
          component: {
            name: 'GiftsExceed',
            data: {
              gifts_exceed_totals: this.gifts_exceed_totals,
            },
          },
        },
      ]

      const fullYearSteps = [
        {
          type: 'question',
          id: 'private_car_costs',
          subtitle: this.$gettext('Private use of car?'),
          question: {
            text: this.$gettext('Would you like to adjust the VAT amount in car costs to reflect private use?'),
            type: 'choose-one',
            options: [
              { text: this.$gettext('Yes'), value: 'true', fn: { name: 'nextStep' } },
              { text: this.$gettext('No'), value: 'false', fn: { name: 'skipStep' } },
            ],
          },
        },
        {
          type: 'subcomponent',
          condition: 'private_car_costs',
          id: 'private_car_costs_component',
          subtitle: this.$gettext('Private use of car?'),
          component: {
            name: 'PrivateCarCosts',
            data: {
              km_business: this.km_total - this.km_private,
              km_private: this.km_private,
              km_total: this.km_total,
              car_costs: this.car_costs,
            },
          },
        },

        {
          type: 'question',
          id: 'private_expenses',
          subtitle: this.$gettext('Private expenses?'),
          question: {
            text: this.$gettext('Do you have any private expenses?'),
            type: 'choose-one',
            options: [
              { text: this.$gettext('Yes'), value: 'true', fn: { name: 'nextStep' } },
              { text: this.$gettext('No'), value: 'false', fn: { name: 'skipStep' } },
            ],
          },
        },
        {
          type: 'subcomponent',
          condition: 'private_expenses',
          subtitle: this.$gettext('Private expenses?'),
          id: 'private_expenses_component',
          component: {
            name: 'PrivateExpenses'
          },
        },
        {
          type: 'message',
          subtitle: this.$gettext('Summary'),
          question: {
            text: this.$gettext(
              'A correction report will also be calculated. Corrections include any data that has not been reflected in previous reports.'
            ),
          },
        },
      ]

      const partialFinalStep = [
        {
          type: 'message',
          subtitle: this.$gettext('Summary'),
          question: {
            text: this.$gettext(
              'We have all the required information, please verify the following data and press Generate Report to complete the process.'
            ),
          },
        },
      ]

      if (this.isFullYear) {
        steps.push(...fullYearSteps)
      } else {
        steps.push(...partialFinalStep)
      }

      if (this.incomplete_costs) {
        steps.unshift({
          type: 'question',
          question: {
            text: this.$gettext(
              'You have incomplete costs in the selected period. Expenses could therefore be incorrectly reflected in your returns. Are you sure you want to continue?'
            ),
            type: 'dialog',
            options: [
              {
                text: this.$gettext('Cancel'),
                variant: 'danger',
                fn: {
                  name: 'close',
                },
              },
              {
                text: this.$gettext('Continue'),
                variant: 'primary',
                fn: {
                  name: 'nextStep',
                },
              },
            ],
          },
        })
      }

      this.storeSetSteps(steps)
    },
  },
}
</script>

<style lang="scss">
.survey-modal {
  .survey-step-container {
    position: relative;
    overflow: hidden;
    will-change: height;
    transition: height 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .survey-step-panel {
    width: 100%;
  }

  .survey-step-enter-active,
  .survey-step-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .survey-step-enter,
  .survey-step-leave-to {
    opacity: 0;
    transform: translateY(8px);
  }

  .survey-modal-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .gk-modal-wrapper {
    .gk-modal {
      margin-top: 10vh;
      .titlebar.title-modal {
        h2 {
          font-size: 22px;
          width: 100%;
        }
        .title-wrap {
          .pagetitle {
            width: 100%;
            &--main {
              width: 100%;
            }
          }
          .action-bar {
            display: none;
          }
        }
      }
      .action-buttons-wrapper.has-actions {
        padding-top: 0;
      }
    }
  }
  .answer-list-wrap {
    font-size: 16px;
    padding: 0.5rem;
    padding-top: 1rem;
    .answer-list {
      list-style: none;
      margin: 0;
      padding: 0;
      font-family: $fontMedium;
      .subanswer {
        font-size: 14px;
        .answer-list--question,
        .answer-list--answer {
          font-family: $fontRegular;
        }
      }
      .hide-answer {
        display: none;
      }
      li {
        &:not(:last-child) {
          margin-bottom: 0.5rem;
        }
        display: flex;
        gap: 1rem;
      }
      &--question {
        flex: 1;
        text-align: right;
      }
      &--answer {
        flex: 1;
        text-align: left;
      }
    }
  }
  .generate-report-btn {
    &.disabled {
      opacity: 0.3 !important;
      filter: grayscale(1);
    }
  }
}
</style>
